import React from "react";
import { useIsMobile } from "../hooks/useIsMobile";

/**
 * Segmented tab bar — the house `.tab-pill-bar` pattern from responsive.css,
 * wrapped so pages stop hand-rolling it.
 *
 * The track **scrolls horizontally instead of wrapping**. Candidates has 7
 * status tabs; as free-standing pills with `flexWrap: "wrap"` they took three
 * rows on a 400px screen and pushed the first result ~200px down the page.
 * `.tab-pill-bar` is already `overflow-x: auto` + `flex-wrap: nowrap` with the
 * scrollbar hidden and a mobile edge-fade (`.tab-pill-bar-wrap::after`), which
 * is the same treatment Gmail, Linear and the App Store give filter strips.
 *
 * Ten pages already use these classes (Attendance, Leave, Timesheet, Expenses,
 * Invoices, Settings, …). Candidates and Interview Hub were the outliers.
 *
 * Styling lives in responsive.css — only the count badge is added here, since
 * the CSS has no rule for it.
 */

export type TabPillItem = {
  key: string;
  label: string;
  count?: number;
};

export function TabPillBar({
  items,
  active,
  onSelect,
  ariaLabel = "Filter",
  style,
}: {
  items: TabPillItem[];
  active: string;
  onSelect: (key: string) => void;
  ariaLabel?: string;
  /** Escape hatch for margin only — do not restyle the track here. */
  style?: React.CSSProperties;
}) {
  const isMobile = useIsMobile(768);
  return (
    <div className="tab-pill-bar-wrap">
      <div className="tab-pill-bar" role="tablist" aria-label={ariaLabel} style={style}>
        {items.map(it => {
          const isActive = it.key === active;
          return (
            <button
              key={it.key}
              role="tab"
              aria-selected={isActive}
              className={isActive ? "active" : ""}
              onClick={() => onSelect(it.key)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                // `.tab-pill-bar button` computes to 36px, below the 44px the
                // design system requires under 768px. The pills this replaced
                // on Interview Hub set 44 explicitly; keep that rather than
                // regress. Scoped here rather than in responsive.css so the ten
                // other pages on these classes aren't restyled by side effect.
                minHeight: isMobile ? 44 : undefined,
              }}>
              {it.label}
              {it.count != null && (
                <span style={{
                  borderRadius: 999, padding: "1px 7px", fontSize: 11, fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  background: isActive ? "#eff6ff" : "#e2e8f0",
                  color: isActive ? "#1d4ed8" : "#64748b",
                }}>{it.count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default TabPillBar;
